import React, { useState } from 'react';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Alert,
  Space,
  Typography,
  Divider,
  Descriptions,
  Button,
  message,
} from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { trpc } from '../../../../lib/trpc';
import { withDevModalFilePath } from '../../../../components/common';

const { Text, Link } = Typography;

interface CreateBankTransferTestModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  companyId: string;
  planCode: string;
  customerEmail?: string;
  predictionAddonQuantity: number;
  userAddonQuantity: number;
  daysUntilDue: number;
}

interface BankTransferInstructions {
  bankName?: string;
  branchName?: string;
  accountNumber?: string;
  accountName?: string;
  amount?: number;
  currency?: string;
  dueDate?: string;
}

interface StripeLinks {
  invoice: string | null;
  subscription: string | null;
  customer: string | null;
}

interface CreateResult {
  success: boolean;
  subscriptionId: string;
  customerId: string;
  invoiceId: string;
  invoiceHostedUrl?: string;
  status: string;
  bankTransferInstructions?: BankTransferInstructions;
  nextInvoiceDate?: string;
  stripeLinks: StripeLinks;
  createdBy: {
    adminId?: string;
    adminEmail?: string;
    createdAt: string;
  };
}

const PLAN_OPTIONS = [
  { value: 'starter', label: 'スターター' },
  { value: 'standard', label: 'スタンダード' },
  { value: 'professional', label: 'プロフェッショナル' },
  { value: 'enterprise', label: 'エンタープライズ' },
  { value: 'enterprise_premium', label: 'エンタープライズプレミアム' },
];

const { Modal } = withDevModalFilePath(
  'client/src/pages/admin/subscriptions/components/CreateBankTransferTestModal.tsx'
);

export function CreateBankTransferTestModal({
  open,
  onClose,
  onSuccess,
}: CreateBankTransferTestModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [result, setResult] = useState<CreateResult | null>(null);

  // 会社一覧取得
  const companiesQuery = trpc.subscriptionAdmin.list.useQuery(undefined, {
    enabled: open,
  });

  const createMutation = trpc.subscriptionAdmin.createTestBankTransferSubscription.useMutation({
    onSuccess: (data) => {
      setResult(data as CreateResult);
      message.success('銀行振込サブスクリプションを作成しました');
    },
    onError: (error) => {
      message.error(`作成に失敗しました: ${error.message}`);
    },
  });

  const handleSubmit = async (values: FormValues) => {
    await createMutation.mutateAsync({
      companyId: values.companyId,
      planCode: values.planCode,
      customerEmail: values.customerEmail || undefined,
      predictionAddonQuantity: values.predictionAddonQuantity || 0,
      userAddonQuantity: values.userAddonQuantity || 0,
      daysUntilDue: values.daysUntilDue,
    });
  };

  const handleClose = () => {
    form.resetFields();
    setResult(null);
    onClose();
  };

  const handleDone = () => {
    form.resetFields();
    setResult(null);
    onSuccess();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('コピーしました');
  };

  // 結果表示モード
  if (result) {
    return (
      <Modal
        title="銀行振込サブスクリプション作成完了"
        open={open}
        onCancel={handleClose}
        footer={[
          <Button key="close" onClick={handleClose}>
            閉じる
          </Button>,
          <Button key="done" type="primary" onClick={handleDone}>
            完了して一覧を更新
          </Button>,
        ]}
        width={700}
      >
        <Alert
          type="success"
          message="サブスクリプションが作成されました"
          description="Stripe Dashboard で請求書を「支払い済み」にマークすると、Webhook経由で契約がactiveになります。"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Subscription ID">
            <Space>
              <Text code>{result.subscriptionId}</Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(result.subscriptionId)}
              />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Customer ID">
            <Space>
              <Text code>{result.customerId}</Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(result.customerId)}
              />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Invoice ID">
            <Space>
              <Text code>{result.invoiceId}</Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(result.invoiceId)}
              />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="ステータス">
            <Text>{result.status}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="次回請求日">
            <Text>{result.nextInvoiceDate ?? '-'}</Text>
          </Descriptions.Item>
        </Descriptions>

        {result.bankTransferInstructions && (
          <>
            <Divider>振込先情報</Divider>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="銀行名">
                {result.bankTransferInstructions.bankName}
              </Descriptions.Item>
              <Descriptions.Item label="支店名">
                {result.bankTransferInstructions.branchName}
              </Descriptions.Item>
              <Descriptions.Item label="口座番号">
                {result.bankTransferInstructions.accountNumber}
              </Descriptions.Item>
              <Descriptions.Item label="口座名義">
                {result.bankTransferInstructions.accountName}
              </Descriptions.Item>
              <Descriptions.Item label="金額">
                ¥{result.bankTransferInstructions.amount?.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="支払期限">
                {result.bankTransferInstructions.dueDate}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        <Divider>Stripe Dashboard リンク</Divider>
        <Space direction="vertical" style={{ width: '100%' }}>
          {result.stripeLinks.invoice && (
            <Link href={result.stripeLinks.invoice} target="_blank">
              <LinkOutlined /> Invoice を開く（ここで「支払い済み」にマーク）
            </Link>
          )}
          {result.stripeLinks.subscription && (
            <Link href={result.stripeLinks.subscription} target="_blank">
              <LinkOutlined /> Subscription を開く
            </Link>
          )}
          {result.stripeLinks.customer && (
            <Link href={result.stripeLinks.customer} target="_blank">
              <LinkOutlined /> Customer を開く
            </Link>
          )}
          {result.invoiceHostedUrl && (
            <Link href={result.invoiceHostedUrl} target="_blank">
              <LinkOutlined /> 顧客向け請求書ページを開く
            </Link>
          )}
        </Space>

        <Divider>次のステップ</Divider>
        <Alert
          type="info"
          message="テストフロー"
          description={
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>上記の Invoice リンクをクリック</li>
              <li>Stripe Dashboard で「Mark as paid」（支払い済みにマーク）をクリック</li>
              <li>Webhook (invoice.paid) がサーバーに届く</li>
              <li>契約ステータスが「active」に変わることを確認</li>
            </ol>
          }
        />
      </Modal>
    );
  }

  // 入力フォームモード
  return (
    <Modal
      title="銀行振込サブスクリプション作成（テスト用）"
      open={open}
      onCancel={handleClose}
      onOk={() => form.submit()}
      confirmLoading={createMutation.isPending}
      width={600}
      okText="作成"
      cancelText="キャンセル"
    >
      <Alert
        type="warning"
        message="テスト環境専用"
        description="この機能は Stripe Test Mode でのデバッグ・検証用です。実際の銀行振込サブスクリプションを作成します。"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          predictionAddonQuantity: 0,
          userAddonQuantity: 0,
          daysUntilDue: 30,
        }}
      >
        <Form.Item
          name="companyId"
          label="会社"
          rules={[{ required: true, message: '会社を選択してください' }]}
        >
          <Select
            showSearch
            placeholder="会社を選択"
            loading={companiesQuery.isLoading}
            optionFilterProp="label"
            options={companiesQuery.data?.map((company) => ({
              value: company.id,
              label: `${company.name} (${company.subscriptionPlan ?? 'プランなし'})`,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="planCode"
          label="プラン"
          rules={[{ required: true, message: 'プランを選択してください' }]}
        >
          <Select placeholder="プランを選択" options={PLAN_OPTIONS} />
        </Form.Item>

        <Form.Item
          name="customerEmail"
          label="メールアドレス（オプション）"
          extra="未入力の場合は会社のメールアドレスを使用"
        >
          <Input type="email" placeholder="test@example.com" />
        </Form.Item>

        <Space style={{ width: '100%' }} size="large">
          <Form.Item name="predictionAddonQuantity" label="予測枠アドオン数">
            <InputNumber min={0} max={100} style={{ width: 120 }} />
          </Form.Item>

          <Form.Item name="userAddonQuantity" label="ユーザー枠アドオン数">
            <InputNumber min={0} max={100} style={{ width: 120 }} />
          </Form.Item>
        </Space>

        <Form.Item
          name="daysUntilDue"
          label="支払期限（日数）"
          rules={[{ required: true, message: '支払期限を入力してください' }]}
        >
          <Space.Compact>
            <InputNumber min={1} max={90} style={{ width: 120 }} />
            <Button disabled style={{ pointerEvents: 'none' }}>日</Button>
          </Space.Compact>
        </Form.Item>
      </Form>
    </Modal>
  );
}
